import React, { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import { User, AuthContextType } from '../model/Model';
import { 
    login as loginService, 
    logout as logoutService, 
    getStoredAccessToken, 
    getStoredRefreshToken,
    getCurrentUser,
    isRefreshTokenExpired,
    refreshAccessToken as refreshTokenService,
    shouldRefreshToken
} from '../service/auth/auth.service';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
    children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
    const [user, setUser] = useState<User | null>(null);
    const [accessToken, setAccessToken] = useState<string | null>(null);
    const [refreshToken, setRefreshToken] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    const logout = useCallback(async () => {
        try {
            await logoutService();
        } catch (error) {
            console.error('Erreur lors de la déconnexion:', error);
        } finally {
            setAccessToken(null);
            setRefreshToken(null);
            setUser(null);
        }
    }, []);

    const refreshAccessToken = useCallback(async (): Promise<boolean> => {
        try {
            const response = await refreshTokenService();
            if (response) {
                setAccessToken(response.accessToken);
                setRefreshToken(response.refreshToken);
                return true;
            }
            return false;
        } catch (error) {
            console.error('Erreur lors du refresh du token:', error);
            await logout();
            return false;
        }
    }, [logout]);

    const login = async (email: string, password: string): Promise<boolean> => {
        try {
            const response = await loginService(email, password);
            setAccessToken(response.accessToken);
            setRefreshToken(response.refreshToken);
            
            // Récupérer les infos utilisateur depuis l'API
            const currentUser = await getCurrentUser();
            if (currentUser) {
                setUser(currentUser);
            }
            
            return true;
        } catch (error) {
            console.error('Erreur de connexion:', error);
            // Nettoyer tout état en cas d'erreur pour éviter les valeurs résiduelles
            setAccessToken(null);
            setRefreshToken(null);
            setUser(null);
            // Re-lancer l'erreur pour que le composant Login puisse la gérer
            throw error;
        }
    };

    useEffect(() => {
        const initializeAuth = async () => {
            setIsLoading(true);
            
            const storedAccessToken = getStoredAccessToken();
            const storedRefreshToken = getStoredRefreshToken();

            // Cas 1: Refresh token expiré - déconnexion complète
            if (storedRefreshToken && isRefreshTokenExpired()) {
                console.log('Refresh token expiré, déconnexion automatique');
                await logout();
            }
            // Cas 2: Access token manquant mais refresh token valide - reconnexion automatique
            else if (!storedAccessToken && storedRefreshToken && !isRefreshTokenExpired()) {
                console.log('Reconnexion automatique avec refresh token');
                const success = await refreshAccessToken();
                if (success) {
                    setAccessToken(getStoredAccessToken());
                    setRefreshToken(getStoredRefreshToken());
                    
                    // Récupérer les infos utilisateur depuis l'API
                    const currentUser = await getCurrentUser();
                    if (currentUser) {
                        setUser(currentUser);
                    } else {
                        await logout();
                    }
                } else {
                    await logout();
                }
            }
            // Cas 3: Access token présent mais expiré - refresh automatique
            else if (storedAccessToken && storedRefreshToken && shouldRefreshToken()) {
                console.log('Rafraîchissement automatique du token');
                const success = await refreshAccessToken();
                if (success) {
                    setAccessToken(getStoredAccessToken());
                    setRefreshToken(getStoredRefreshToken());
                    
                    // Récupérer les infos utilisateur depuis l'API
                    const currentUser = await getCurrentUser();
                    if (currentUser) {
                        setUser(currentUser);
                    } else {
                        await logout();
                    }
                } else {
                    await logout();
                }
            }
            // Cas 4: Token d'accès valide - récupérer les infos utilisateur
            else if (storedAccessToken && storedRefreshToken) {
                console.log('Récupération des infos utilisateur depuis l\'API');
                setAccessToken(storedAccessToken);
                setRefreshToken(storedRefreshToken);
                
                // Toujours récupérer depuis l'API pour avoir les infos à jour
                const currentUser = await getCurrentUser();
                if (currentUser) {
                    setUser(currentUser);
                } else {
                    // Si impossible de récupérer l'utilisateur, déconnecter
                    await logout();
                }
            }
            // Cas 5: Aucun token valide - état déconnecté
            else {
                console.log('Aucun token valide, utilisateur déconnecté');
            }
            
            setIsLoading(false);
        };

        initializeAuth();
    }, [logout, refreshAccessToken]);

    const value: AuthContextType = {
        user,
        setUser,
        accessToken,
        refreshToken,
        login,
        logout,
        refreshAccessToken,
        isAuthenticated: !!accessToken && !!user,
        isLoading
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth(): AuthContextType {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}